import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { formatDateTime } from '../../../utils/Utils';

export const INTERVIEW_TYPES = ['phone', 'video', 'onsite', 'panel', 'technical', 'culture'];
export const INTERVIEW_STATUSES = ['scheduled', 'completed', 'cancelled', 'rescheduled', 'no_show'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useAtsInterviewsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/ats-interviews?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const complete = useCallback(async (id, notes) => {
    try { await api.post(`/v1/ats-interviews/${id}/complete`, { notes }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const cancel = useCallback(async (id, reason) => {
    try { await api.post(`/v1/ats-interviews/${id}/cancel`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const reschedule = useCallback(async (id, body) => {
    try { await api.post(`/v1/ats-interviews/${id}/reschedule`, body); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const markNoShow = useCallback(async (id, side) => {
    try { await api.post(`/v1/ats-interviews/${id}/no-show`, { side }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const remove = useCallback(async (id) => {
    try { await api.delete(`/v1/ats-interviews/${id}`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const typeLabel = (s) => t(`ats.interview.type.${s}`, { defaultValue: s });
    const statusLabel = (s) => t(`ats.interview.status.${s}`, { defaultValue: s });

    // Sıralama: backend AtsInterviewDataAccess orderBy switch'i scheduledstart | status —
    // `sortKey` bunlara eşler; diğer kolonlar `sort: false`.
    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      // Satır tıklaması başvuru detayına götürür (FE-28) — kolon gizli taşınır.
      { name: 'applicationId', label: 'AppId', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'candidateFullName',
        label: t('ats.interview.candidate'),
        options: {
          filter: true,
          sort: false,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'candidateName[like]' },
        },
      },
      {
        name: 'jobPostingTitle',
        label: t('ats.interview.jobPosting'),
        options: { filter: false, sort: false },
      },
      {
        name: 'interviewType',
        label: t('ats.interview.typeLabel'),
        options: {
          ...multiSelectFilter(t('ats.interview.typeLabel'), INTERVIEW_TYPES, typeLabel),
          sort: false,
          customBodyRender: (v) => typeLabel(v),
        },
      },
      {
        name: 'status',
        label: t('ats.interview.statusLabel'),
        options: {
          ...multiSelectFilter(t('ats.interview.statusLabel'), INTERVIEW_STATUSES, statusLabel),
          sortKey: 'status',
          customBodyRender: (v) => statusLabel(v),
        },
      },
      dateFilterColumn('scheduledStart', t('ats.interview.scheduledStart'), lang, { includeTime: true, sortKey: 'scheduledstart' }),
      {
        name: 'scheduledEnd',
        label: t('ats.interview.scheduledEnd'),
        options: { filter: false, sort: false, customBodyRender: (v) => formatDateTime(v, lang) },
      },
      {
        name: 'location',
        label: t('ats.interview.location'),
        options: {
          filter: true,
          sort: false,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
          customBodyRender: (v) => v || '-',
        },
      },
      {
        name: 'candidateConfirmed',
        label: t('ats.interview.confirmed'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v) => v ? '✓' : '-',
        },
      },
      {
        // FE-28 — satır artık katılımcı ADLARINI gösterir (DTO `name` taşıyor).
        // Eskiden yalnız sayı yazıyordu ve "kimin skorkartı bekleniyor" sorusu
        // cevapsız kalıyordu. Ad çözülemeyen satır (silinmiş kişi) sayıya düşer.
        name: 'participants',
        label: t('ats.interview.schedule.participantsLabel'),
        options: {
          filter: false, sort: false, download: false,
          customBodyRender: (v) => {
            if (!Array.isArray(v) || v.length === 0) return '-';
            const names = v.map((p) => p?.name).filter(Boolean);
            return names.length > 0 ? names.join(', ') : String(v.length);
          },
        },
      },
    ];
  }, [t, lang]);

  return { fetching, data, pagination, columns, refresh, complete, cancel, reschedule, markNoShow, remove };
};

export default useAtsInterviewsApi;
